/**
 * Module Planning (mode classique) — Editorial Operations (HiFi.6).
 *
 * Refonte visuelle complète. Le port Phase 1 affichait un calendrier
 * mensuel + 2 cartes Aujourd'hui/À venir ; le design Editorial
 * Operations privilégie une **vue semaine focused** :
 *
 *   1. `EditorialHero` : badge + dateLine « Semaine N » + titre
 *      « Du *X* au *Y mois*. »
 *   2. **Week strip** : 7 boutons jour (Lun-Dim de la semaine ISO
 *      courante). Jour actif en `--accent-deep`, jour today
 *      surligné. Marker dot sous le numéro si le jour a du contenu.
 *   3. Actions row : « + Événement » / « + Tâche ».
 *   4. `SectionHead` « {Jour} {N mois} » + count à droite.
 *   5. Card de rows (events via `listRow`, tasks via row custom
 *      avec checkbox toggle inline — `task-toggle-{id}` préservé).
 *   6. `SectionHead` « À venir » + Card si items futurs (testid
 *      `planning-upcoming` préservé pour le test 89-103).
 *   7. `AiSuggestion` bienveillante.
 *
 * Les modales d'ajout (event + task) restent fonctionnellement
 * identiques — leur visuel passe par le composant `ui/Modal.ts`.
 *
 * Toutes les chaînes UI passent par `t(key)`. Données via
 * `getCurrentHome().data` (CLAUDE.md §2.2).
 */
import { html, type TemplateResult } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './View.module.css';
import { getLang, t, tn, type TKey } from '../../i18n/index.js';
import { addEvent, addTask, getCurrentHome, toggleTask } from '../../stores/homes.js';
import { bumpUi, closeModal, getModal, openModal } from '../../stores/ui.js';
import { todayIso } from '../../lib/date.js';
import {
  buildPlanningItems,
  daysWithContent,
  todayItems,
  upcomingItems,
  type PlanningItem,
} from './logic.js';
import type { ClassicHomeData, FamilyEvent, FamilyTask, Member } from '@nestor/shared-types';
import { modalTemplate } from '../../ui/Modal.js';
import { editorialHero } from '../../ui/Hero.js';
import { sectionHead } from '../../ui/SectionHead.js';
import { listRow } from '../../ui/ListRow.js';

// ── Constantes ────────────────────────────────────────────────────────

const MODAL_EVENT = 'planning:event';
const MODAL_TASK = 'planning:task';

const WEEKDAY_KEYS: readonly TKey[] = [
  'weekday_mon',
  'weekday_tue',
  'weekday_wed',
  'weekday_thu',
  'weekday_fri',
  'weekday_sat',
  'weekday_sun',
];

const MEMBER_PALETTE = ['#7A9E8E', '#5B6C8F', '#C49060', '#9B7CB6', '#5B8DB8', '#D27C63'];

function memberColor(id: string | undefined): string {
  if (!id) return 'var(--ink-faint)';
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_PALETTE[hash % MEMBER_PALETTE.length]!;
}

function memberAuthorKicker(
  members: readonly Member[],
  whoId: string | undefined,
): string | undefined {
  if (!whoId) return undefined;
  const m = members.find((mm) => mm.id === whoId);
  return m?.name.split(/\s+/)[0]?.toUpperCase();
}

// ── Sélection de jour (état local au module) ──────────────────────────

/**
 * Jour sélectionné par l'utilisateur dans le week strip. ISO `YYYY-MM-DD`.
 * `null` ou jour hors semaine courante → l'auto-reset au today courant
 * à chaque render.
 */
let selectedDay: string | null = null;
// Navigation de semaine (F1) : 0 = semaine courante, -1 = précédente,
// +1 = suivante. Permet de consulter/planifier hors de la semaine en
// cours (avant : le planning était enfermé sur la semaine actuelle).
let weekOffset = 0;

function shiftWeek(delta: number): void {
  weekOffset += delta;
  // Aligne le jour sélectionné sur le lundi de la nouvelle semaine.
  const monday = startOfIsoWeek(new Date());
  monday.setDate(monday.getDate() + weekOffset * 7);
  selectedDay = isoOf(monday);
  bumpUi();
}

function goToday(): void {
  weekOffset = 0;
  selectedDay = todayIso();
  bumpUi();
}

function getSelectedDay(): string {
  const today = todayIso();
  // Si rien sélectionné, ou si l'utilisateur a navigué après une longue
  // pause et qu'on est passé à un nouveau jour, on reset à today.
  if (!selectedDay) return today;
  return selectedDay;
}

function setSelectedDay(iso: string): void {
  selectedDay = iso;
}

// ── Computations semaine ──────────────────────────────────────────────

interface WeekDay {
  iso: string; // YYYY-MM-DD
  dayOfMonth: number;
  weekdayKey: TKey; // 'weekday_mon' etc.
  isToday: boolean;
  hasContent: boolean;
}

function startOfIsoWeek(date: Date): Date {
  // Lundi = début de semaine ISO. JS `getDay()` retourne 0=Dim..6=Sam.
  const d = new Date(date);
  const dow = d.getDay();
  const diffToMonday = (dow + 6) % 7; // 0=Mon → 0, 1=Tue → 1, …, 0=Sun → 6
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - diffToMonday);
  return d;
}

function isoOf(date: Date): string {
  return `${date.getFullYear().toString().padStart(4, '0')}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

function buildWeek(items: readonly PlanningItem[]): WeekDay[] {
  const todayIsoStr = todayIso();
  const monday = startOfIsoWeek(new Date());
  monday.setDate(monday.getDate() + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const iso = isoOf(d);
    const contentSet = daysWithContent(items, d.getFullYear(), d.getMonth() + 1);
    return {
      iso,
      dayOfMonth: d.getDate(),
      weekdayKey: WEEKDAY_KEYS[i]!,
      isToday: iso === todayIsoStr,
      hasContent: contentSet.has(d.getDate()),
    };
  });
}

function localizedDate(iso: string, opts: Intl.DateTimeFormatOptions): string {
  const lang = getLang();
  const d = new Date(`${iso}T00:00:00`);
  try {
    return new Intl.DateTimeFormat(lang, opts).format(d);
  } catch {
    return iso;
  }
}

// ── Templates ─────────────────────────────────────────────────────────

function weekStripTemplate(week: readonly WeekDay[], selected: string): TemplateResult {
  return html`<div class=${styles.weekStrip} data-testid="planning-calendar">
    ${week.map(
      (d) =>
        html`<button
          type="button"
          class=${classMap({
            [styles.dayBtn!]: true,
            [styles.dayBtnActive!]: d.iso === selected,
            [styles.dayBtnToday!]: d.isToday && d.iso !== selected,
          })}
          data-testid="cal-day-${d.dayOfMonth}"
          data-day-iso=${d.iso}
          @click=${(): void => {
            setSelectedDay(d.iso);
          }}
        >
          <span class=${styles.dayKicker}>${t(d.weekdayKey)}</span>
          <span class=${styles.dayNumber}>${d.dayOfMonth}</span>
          ${d.hasContent ? html`<span class=${styles.dayMark}></span>` : null}
        </button>`,
    )}
  </div>`;
}

function eventRow(
  homeId: string,
  e: FamilyEvent,
  members: readonly Member[],
  last: boolean,
): TemplateResult {
  void homeId;
  return listRow({
    time: e.time ?? '·',
    title: e.title,
    ...(e.location ? { meta: e.location } : {}),
    rowTag: { label: t('tag_event_label'), kind: 'event' },
    dotColor: memberColor(e.who),
    ...(memberAuthorKicker(members, e.who)
      ? { author: memberAuthorKicker(members, e.who) as string }
      : {}),
    last,
    testid: 'planning-event-row',
  });
}

function taskRow(
  homeId: string,
  task: FamilyTask,
  members: readonly Member[],
  last: boolean,
): TemplateResult {
  const onToggle = (ev: Event): void => {
    ev.preventDefault();
    ev.stopPropagation();
    toggleTask(homeId, task.id);
  };
  const author = memberAuthorKicker(members, task.who);
  return html`<div
    class=${classMap({
      [styles.taskRow!]: true,
      [styles.taskRowLast!]: last,
    })}
    data-testid="planning-task-row"
    data-task-id=${task.id}
  >
    <button
      type="button"
      role="checkbox"
      aria-checked=${task.done ? 'true' : 'false'}
      class=${classMap({
        [styles.taskToggle!]: true,
        [styles.taskToggleDone!]: task.done,
      })}
      aria-label=${task.text}
      data-testid="task-toggle-${task.id}"
      @click=${onToggle}
    >
      ${task.done ? '✓' : ''}
    </button>
    <span class=${styles.taskTime}>${task.due ? '·' : '·'}</span>
    <span class=${styles.taskTitleWrap}>
      <div
        class=${classMap({
          [styles.taskTitle!]: true,
          [styles.taskTitleDone!]: !!task.done,
        })}
      >
        ${task.text}
      </div>
      ${task.due ? html`<div class=${styles.taskMeta}>${task.due}</div>` : null}
    </span>
    <span class="kicker">${t('tag_task_label')}</span>
    ${author ? html`<span class=${styles.taskAuthor}>${author}</span>` : html`<span></span>`}
    <span></span>
  </div>`;
}

function planningRow(
  homeId: string,
  item: PlanningItem,
  members: readonly Member[],
  last: boolean,
): TemplateResult {
  return item.type === 'event'
    ? eventRow(homeId, item.data, members, last)
    : taskRow(homeId, item.data, members, last);
}

function dayCard(
  homeId: string,
  items: PlanningItem[],
  members: readonly Member[],
  testid: string,
): TemplateResult {
  if (items.length === 0) {
    return html`<div class=${styles.card} data-testid=${testid}>
      <div class=${styles.cardEmpty}>${t('nothing_planned')}</div>
    </div>`;
  }
  return html`<div class=${styles.card} data-testid=${testid}>
    ${items.map((it, idx) => planningRow(homeId, it, members, idx === items.length - 1))}
  </div>`;
}

function eventFormTemplate(homeId: string, members: readonly Member[]): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const title = (form.elements.namedItem('title') as HTMLInputElement).value;
    const date = (form.elements.namedItem('date') as HTMLInputElement).value;
    const time = (form.elements.namedItem('time') as HTMLInputElement).value;
    const location = (form.elements.namedItem('location') as HTMLInputElement).value;
    const who = (form.elements.namedItem('who') as HTMLSelectElement).value;
    const created = addEvent(homeId, {
      title,
      date,
      ...(time ? { time } : {}),
      ...(location ? { location } : {}),
      ...(who ? { who } : {}),
    });
    if (created) closeModal();
    // Note: on NE bascule PAS automatiquement le jour sélectionné sur
    // la date du nouvel event. L'utilisateur reste sur sa journée
    // courante ; l'event futur apparaît dans la section « À venir »
    // (préservé pour l'UX du test 89-103).
  };
  return html`<form class=${styles.form} @submit=${onSubmit} data-testid="event-form">
    <div class=${styles.field}>
      <label for="ev-title">${t('event_title')}</label>
      <input
        id="ev-title"
        name="title"
        type="text"
        required
        maxlength="200"
        data-testid="ev-title"
      />
    </div>
    <div class=${styles.fieldInline}>
      <div class=${styles.field}>
        <label for="ev-date">${t('event_date')}</label>
        <input
          id="ev-date"
          name="date"
          type="date"
          required
          value=${todayIso()}
          data-testid="ev-date"
        />
      </div>
      <div class=${styles.field}>
        <label for="ev-time">${t('event_time')}</label>
        <input id="ev-time" name="time" type="time" data-testid="ev-time" />
      </div>
    </div>
    <div class=${styles.field}>
      <label for="ev-location">${t('event_location')}</label>
      <input
        id="ev-location"
        name="location"
        type="text"
        maxlength="200"
        data-testid="ev-location"
      />
    </div>
    <div class=${styles.field}>
      <label for="ev-who">${t('who')}</label>
      <select id="ev-who" name="who" data-testid="ev-who">
        <option value="">${t('unassigned')}</option>
        ${members.map((m) => html`<option value=${m.id}>${m.name}</option>`)}
      </select>
    </div>
    <div class=${styles.formActions}>
      <button type="button" class=${styles.cancel} @click=${(): void => closeModal()}>
        ${t('cancel')}
      </button>
      <button type="submit" class=${styles.submit} data-testid="ev-submit">${t('save')}</button>
    </div>
  </form>`;
}

function taskFormTemplate(homeId: string, members: readonly Member[]): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const text = (form.elements.namedItem('text') as HTMLInputElement).value;
    const due = (form.elements.namedItem('due') as HTMLInputElement).value;
    const who = (form.elements.namedItem('who') as HTMLSelectElement).value;
    const created = addTask(homeId, {
      text,
      ...(due ? { due } : {}),
      ...(who ? { who } : {}),
    });
    if (created) closeModal();
  };
  return html`<form class=${styles.form} @submit=${onSubmit} data-testid="task-form">
    <div class=${styles.field}>
      <label for="tk-text">${t('task_text')}</label>
      <input id="tk-text" name="text" type="text" required maxlength="500" data-testid="tk-text" />
    </div>
    <div class=${styles.fieldInline}>
      <div class=${styles.field}>
        <label for="tk-due">${t('task_due')}</label>
        <input id="tk-due" name="due" type="date" data-testid="tk-due" />
      </div>
      <div class=${styles.field}>
        <label for="tk-who">${t('who')}</label>
        <select id="tk-who" name="who" data-testid="tk-who">
          <option value="">${t('unassigned')}</option>
          ${members.map((m) => html`<option value=${m.id}>${m.name}</option>`)}
        </select>
      </div>
    </div>
    <div class=${styles.formActions}>
      <button type="button" class=${styles.cancel} @click=${(): void => closeModal()}>
        ${t('cancel')}
      </button>
      <button type="submit" class=${styles.submit} data-testid="tk-submit">${t('save')}</button>
    </div>
  </form>`;
}

// ── Vue principale ────────────────────────────────────────────────────

export function renderPlanningView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'classic') {
    return html`<section data-testid="view-planning" class=${styles.view}></section>`;
  }
  const data = home.data as ClassicHomeData;
  const items = buildPlanningItems(data.events, data.tasks);
  const today = todayIso();
  const week = buildWeek(items);
  const selected = getSelectedDay();
  const dayItems = items.filter((i) => i.date === selected);
  const todays = todayItems(items, today);
  const upcoming = upcomingItems(items, today, 8);
  const modal = getModal();

  // Date-line + accent du hero : « Du *X* au *Y mois*. »
  const first = week[0]!;
  const last = week[6]!;
  const weekYear = first.iso.slice(0, 4);
  const weekNum = ((): number => {
    // ISO 8601 week — Thursday of the week determines the year
    const d = new Date(first.iso);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  })();
  const heroAccent = `${first.dayOfMonth.toString()} ${t('planning_du_au_join')} ${last.dayOfMonth.toString()} ${localizedDate(last.iso, { month: 'long' })}`;
  // Compose dateLine "Semaine N · 2026"
  const dateLine = `${t('planning_week_label')} ${weekNum.toString()} · ${weekYear}`;
  // Section head right : count
  const dayLabel = localizedDate(selected, { weekday: 'short', day: 'numeric', month: 'short' });

  // Bouton "Aujourd'hui" reste accessible via le sélecteur.
  const selectedIsTodayRow = selected === today;

  return html`
    <section data-testid="view-planning" class=${styles.view}>
      ${editorialHero({
        badge: { mode: 'classic', label: home.name },
        dateLine,
        title: t('planning_hero_title'),
        accent: `${t('planning_from_word').toLowerCase()} ${heroAccent}`,
      })}
      <div class=${styles.weekNav} data-testid="planning-week-nav">
        <button
          type="button"
          class=${styles.weekNavBtn}
          aria-label=${t('planning_prev_week')}
          data-testid="week-prev"
          @click=${(): void => shiftWeek(-1)}
        >
          ‹
        </button>
        <button
          type="button"
          class=${styles.weekNavToday}
          data-testid="week-today"
          @click=${goToday}
        >
          ${t('today')}
        </button>
        <button
          type="button"
          class=${styles.weekNavBtn}
          aria-label=${t('planning_next_week')}
          data-testid="week-next"
          @click=${(): void => shiftWeek(1)}
        >
          ›
        </button>
      </div>
      ${weekStripTemplate(week, selected)}
      <div class=${styles.actions}>
        <button
          type="button"
          class=${styles.btnPrimary}
          data-testid="btn-add-event"
          @click=${(): void => openModal(MODAL_EVENT)}
        >
          ${t('add_event')}
        </button>
        <button
          type="button"
          class=${styles.btnSecondary}
          data-testid="btn-add-task"
          @click=${(): void => openModal(MODAL_TASK)}
        >
          ${t('add_task')}
        </button>
      </div>
      ${sectionHead({
        kicker: selectedIsTodayRow ? t('today') : dayLabel,
        right: `${dayItems.length.toString()} ${tn('home_entry', dayItems.length)}`,
        testid: 'planning-section-day',
      })}
      ${dayCard(home.id, selectedIsTodayRow ? todays : dayItems, data.members, 'planning-today')}
      ${upcoming.length > 0
        ? html`${sectionHead({
            kicker: t('upcoming'),
            right: `${upcoming.length.toString()} ${tn('home_entry', upcoming.length)}`,
            testid: 'planning-section-upcoming',
          })}
          ${dayCard(home.id, upcoming, data.members, 'planning-upcoming')}`
        : null}
      ${modal === MODAL_EVENT
        ? modalTemplate({
            title: t('add_event'),
            content: eventFormTemplate(home.id, data.members),
            onClose: closeModal,
            closeLabel: t('close'),
            testid: 'modal-event',
          })
        : null}
      ${modal === MODAL_TASK
        ? modalTemplate({
            title: t('add_task'),
            content: taskFormTemplate(home.id, data.members),
            onClose: closeModal,
            closeLabel: t('close'),
            testid: 'modal-task',
          })
        : null}
    </section>
  `;
}
